import {
  ColumnFiltersState,
  useTable,
  clientAppTableFeatures,
  ColumnDef,
  SortingState,
  VisibilityState,
  TableOptions,
  type ColumnOrderState,
  type ColumnSizingState,
  type OnChangeFn,
  type RowData,
} from '@/lib/tanstack-table';
import { useState } from 'react';

/**
 * Table state the owner keeps instead of the table, e.g. a saved view. Each
 * value is used with its change handler; omitted keys stay table-local.
 */
export interface ControlledTableState {
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>;
}

interface UseDataTableProps<TData extends RowData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  options: Pick<
    TableOptions<TData>,
    | 'initialState'
    | 'enableRowSelection'
    | 'globalFilterFn'
    | 'meta'
    | 'enableMultiSort'
    | 'maxMultiSortColCount'
    | 'enableColumnResizing'
    | 'columnResizeMode'
  >;
  controlled?: ControlledTableState;
}

export function useDataTable<TData extends RowData>({
  columns,
  data,
  options,
  controlled = {},
}: UseDataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    options.initialState?.columnFilters ?? [],
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState(
    options.initialState?.globalFilter ?? '',
  );
  const {
    sorting: controlledSorting,
    onSortingChange,
    columnOrder,
    onColumnOrderChange,
    columnSizing,
    onColumnSizingChange,
  } = controlled;

  return useTable({
    features: clientAppTableFeatures,
    data,
    columns,
    ...options,
    // built-in features
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: onSortingChange ?? setSorting,
    onGlobalFilterChange: setGlobalFilter,
    ...(onColumnOrderChange ? { onColumnOrderChange } : {}),
    ...(onColumnSizingChange ? { onColumnSizingChange } : {}),
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
      sorting: controlledSorting ?? sorting,
      globalFilter,
      ...(columnOrder ? { columnOrder } : {}),
      ...(columnSizing ? { columnSizing } : {}),
    },
  });
}
